import React, { useState, useEffect } from 'react';
import { Upload, GitBranch, TrendingUp, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { apiCall } from './Api';
import { getCurrentToken } from './SessionManager';

const UploadTypeSelector = ({ sessionId, onProceed, onCancel }) => {
  const [uploadType, setUploadType] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [description, setDescription] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessionInfo();
    loadCampaigns();
  }, []);

  const loadSessionInfo = async () => {
    try {
      const token = getCurrentToken();
      const response = await apiCall(`/api/cdp/sessions/${sessionId}/check-subsequent-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSessionInfo(data);
      }
    } catch (error) {
      console.error('Error loading session info:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const token = getCurrentToken();
      const response = await apiCall(`/api/campaigns?session_id=${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const handleProceed = () => {
    if (!uploadType) return;
    if (uploadType === 'campaign_conversion' && !selectedCampaign) return;

    onProceed({
      uploadType,
      campaignId: uploadType === 'campaign_conversion' ? selectedCampaign : null,
      description
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!sessionInfo?.has_existing_table) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-900">No Unified Table Found</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Please complete the initial data unification before uploading additional data.
            </p>
            <button
              onClick={onCancel}
              className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <CheckCircle className="h-5 w-5 text-blue-600" />
          <div>
            <p className="font-medium text-blue-900">Current Version: {sessionInfo.current_version}</p>
            <p className="text-sm text-blue-700">Total Customers: {sessionInfo.total_rows?.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">What type of data are you uploading?</h3>
        
        <div className="space-y-3">
          <label className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${
            uploadType === 'addendum' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="uploadType"
              value="addendum"
              checked={uploadType === 'addendum'}
              onChange={(e) => setUploadType(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-start space-x-3">
              <Upload className={`h-6 w-6 flex-shrink-0 ${uploadType === 'addendum' ? 'text-blue-600' : 'text-gray-400'}`} />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">Data Addendum</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Add new customer records or enrich existing customers with additional attributes
                </p>
                <ul className="text-xs text-gray-500 mt-2 space-y-1">
                  <li>• New customers will be added to the unified table</li>
                  <li>• Existing customers will be enriched with new data fields</li>
                </ul>
              </div>
            </div>
          </label>

          <label className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${
            uploadType === 'campaign_conversion' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="uploadType"
              value="campaign_conversion"
              checked={uploadType === 'campaign_conversion'}
              onChange={(e) => setUploadType(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-start space-x-3">
              <TrendingUp className={`h-6 w-6 flex-shrink-0 ${uploadType === 'campaign_conversion' ? 'text-blue-600' : 'text-gray-400'}`} />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">Campaign Conversion Metrics</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Update customer data with sales/conversion results from a campaign
                </p>
                <ul className="text-xs text-gray-500 mt-2 space-y-1">
                  <li>• Track customer value changes</li>
                  <li>• Attribute conversions to campaigns</li>
                  <li>• Analyze campaign effectiveness</li>
                </ul>
              </div>
            </div>
          </label>
        </div>
      </div>

      {uploadType === 'campaign_conversion' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Campaign *
          </label>
          <select
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Choose a campaign...</option>
            {campaigns.map(campaign => (
              <option key={campaign.campaign_id} value={campaign.campaign_id}>
                {campaign.campaign_name} - {new Date(campaign.created_at).toLocaleDateString()}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description (Optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add notes about this data upload..."
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          rows={3}
        />
      </div>

      <div className="flex items-center justify-end space-x-3 pt-4 border-t">
        <button
          onClick={onCancel}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleProceed}
          disabled={!uploadType || (uploadType === 'campaign_conversion' && !selectedCampaign)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <span>Continue</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const VersionTimeline = ({ sessionId, onRefresh }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      const token = getCurrentToken();
      const response = await apiCall(`/api/cdp/sessions/${sessionId}/versions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setVersions(data.versions || []);
      }
    } catch (error) {
      console.error('Error loading versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async (versionNumber) => {
    if (versionNumber <= 1) return;

    try {
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${sessionId}/versions/${versionNumber}/compare`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setComparison(data);
      }
    } catch (error) {
      console.error('Error loading comparison:', error);
    }
  };

  const handleVersionClick = (version) => {
    setSelectedVersion(version);
    if (version.version_number > 1) {
      loadComparison(version.version_number);
    } else {
      setComparison(null);
    }
  };

  if (loading) {
    return <div className="animate-pulse space-y-3">
      {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-200 rounded"></div>)}
    </div>;
  }

  const getUploadTypeLabel = (type) => {
    const labels = {
      'initial': 'Initial Upload',
      'addendum': 'Data Addendum',
      'campaign_conversion': 'Campaign Conversion'
    };
    return labels[type] || type;
  };

  const getUploadTypeColor = (type) => {
    const colors = {
      'initial': 'bg-blue-100 text-blue-800',
      'addendum': 'bg-green-100 text-green-800',
      'campaign_conversion': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
        <button
          onClick={loadVersions}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-3">
        {versions.map((version, index) => (
          <div
            key={version.version_id}
            onClick={() => handleVersionClick(version)}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedVersion?.version_id === version.version_id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="flex-shrink-0 mt-1">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600">V{version.version_number}</span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getUploadTypeColor(version.upload_type)}`}>
                      {getUploadTypeLabel(version.upload_type)}
                    </span>
                    {version.status === 'purged' && (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                        Purged
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Total Customers</p>
                      <p className="font-medium text-gray-900">{version.total_rows?.toLocaleString()}</p>
                    </div>
                    {version.new_rows > 0 && (
                      <div>
                        <p className="text-gray-500">New</p>
                        <p className="font-medium text-green-600">+{version.new_rows?.toLocaleString()}</p>
                      </div>
                    )}
                    {version.updated_rows > 0 && (
                      <div>
                        <p className="text-gray-500">Updated</p>
                        <p className="font-medium text-blue-600">{version.updated_rows?.toLocaleString()}</p>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(version.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {index < versions.length - 1 && (
                <ArrowRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-2" />
              )}
            </div>

            {selectedVersion?.version_id === version.version_id && comparison && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Changes from V{version.version_number - 1}</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-green-50 rounded p-3">
                    <p className="text-xs text-green-600 font-medium">Added</p>
                    <p className="text-lg font-bold text-green-900">
                      {comparison.customer_changes.new?.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded p-3">
                    <p className="text-xs text-blue-600 font-medium">Updated</p>
                    <p className="text-lg font-bold text-blue-900">
                      {comparison.customer_changes.updated?.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded p-3">
                    <p className="text-xs text-purple-600 font-medium">Total</p>
                    <p className="text-lg font-bold text-purple-900">
                      {comparison.customer_changes.current_total?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {versions.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <GitBranch className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p>No versions yet</p>
        </div>
      )}
    </div>
  );
};

const VersionColumnMapping = ({ sessionId, versionNumber, onComplete, onCancel }) => {
  const [analysis, setAnalysis] = useState(null);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyzeColumns();
  }, []);

  const analyzeColumns = async () => {
    try {
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${sessionId}/analyze-columns`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ version_number: versionNumber })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);

        const initialMappings = {};
        data.auto_matched?.forEach(m => {
          initialMappings[m.source_column] = m.target_column;
        });
        data.new_columns?.forEach(c => {
          initialMappings[c.column_name] = c.column_name;
        });
        setMappings(initialMappings);
      }
    } catch (error) {
      console.error('Error analyzing columns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (sourceColumn, targetColumn) => {
    setMappings(prev => ({ ...prev, [sourceColumn]: targetColumn }));
  };

  const handleConfirm = () => {
    onComplete(mappings);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-blue-600 font-medium">Auto-Matched</p>
            <p className="text-2xl font-bold text-blue-900">{analysis?.summary?.auto_matched_count || 0}</p>
          </div>
          <div>
            <p className="text-yellow-600 font-medium">Needs Review</p>
            <p className="text-2xl font-bold text-yellow-900">{analysis?.summary?.needs_review_count || 0}</p>
          </div>
          <div>
            <p className="text-green-600 font-medium">New Columns</p>
            <p className="text-2xl font-bold text-green-900">{analysis?.summary?.new_columns_count || 0}</p>
          </div>
        </div>
      </div>

      {analysis?.auto_matched && analysis.auto_matched.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Auto-Matched Columns</h3>
          <div className="space-y-2">
            {analysis.auto_matched.map((match, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
                <span className="font-medium text-gray-900">{match.source_column}</span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-green-700">{match.target_column}</span>
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis?.needs_mapping && analysis.needs_mapping.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Columns Needing Review</h3>
          <div className="space-y-3">
            {analysis.needs_mapping.map((item, idx) => (
              <div key={idx} className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {item.source_column}
                </label>
                <select
                  value={mappings[item.source_column] || ''}
                  onChange={(e) => handleMappingChange(item.source_column, e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select mapping...</option>
                  <option value={item.source_column}>Create as new column</option>
                  {item.suggested_matches?.map(match => (
                    <option key={match.column} value={match.column}>
                      {match.column} (Confidence: {Math.round(match.score * 100)}%)
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis?.new_columns && analysis.new_columns.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">New Columns</h3>
          <div className="space-y-2">
            {analysis.new_columns.map((col, idx) => (
              <div key={idx} className="p-3 bg-gray-50 border border-gray-200 rounded">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{col.column_name}</span>
                  <span className="text-sm text-gray-500">{col.data_type}</span>
                </div>
                {col.sample_values && col.sample_values.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Samples: {col.sample_values.slice(0, 3).join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end space-x-3 pt-4 border-t">
        <button
          onClick={onCancel}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Confirm & Process
        </button>
      </div>
    </div>
  );
};

export { UploadTypeSelector, VersionTimeline, VersionColumnMapping };
